import { ZomentumEntities, ZomentumWindow } from "@/Contracts/common";
import {
	MergeTagVariable,
	MergeTagVariableDataType
} from "@/Contracts/Documents";
import { EntityType } from "@/Contracts/Others/EmailThread";
import { ComputeMergeTagProps } from "@/Contracts/Others/MergeTags";
import { TriggerActionEntityTypes } from "@/Contracts/Others/SalesActivity";
import uniq from "lodash/uniq";
import { isVendorApp } from "../common";

export const MERGE_TAG_START = "{{";
export const MERGE_TAG_END = "}}";
export const INPUT_FIELD_TAG_START = "[[";
export const INPUT_FIELD_TAG_END = "]]";

const MERGE_TAG_DATA_TYPES_ASSIGNABLE_TO: Partial<
	Record<MergeTagVariableDataType, MergeTagVariableDataType[]>
> = {
	[MergeTagVariableDataType.TEXT]: [
		MergeTagVariableDataType.TEXT,
		MergeTagVariableDataType.DOUBLE,
		MergeTagVariableDataType.NUMBER,
		MergeTagVariableDataType.DROPDOWN
	],
	[MergeTagVariableDataType.DOUBLE]: [
		MergeTagVariableDataType.DOUBLE,
		MergeTagVariableDataType.NUMBER
	],
	[MergeTagVariableDataType.NUMBER]: [MergeTagVariableDataType.NUMBER]
};

export const filterMergeTagsByDataType = (
	mergeTags: MergeTagVariable[],
	targetDataType?: MergeTagVariableDataType
): MergeTagVariable[] => {
	if (!targetDataType) return mergeTags;

	const assignableDataTypes =
		MERGE_TAG_DATA_TYPES_ASSIGNABLE_TO[targetDataType] ?? [];

	return mergeTags.filter(
		mergeTag =>
			mergeTag.data_type &&
			assignableDataTypes.includes(mergeTag.data_type)
	);
};

export const replaceMergeTagsWithId = (
	mergeTagVariables: MergeTagVariable[],
	text: string
): string => {
	let textToReturn = text;
	if (!mergeTagVariables) mergeTagVariables = [];
	mergeTagVariables.forEach(mergeTag => {
		textToReturn = textToReturn
			?.split(
				`${MERGE_TAG_START}${mergeTag.display_name}${MERGE_TAG_END}`
			)
			?.join(
				`${MERGE_TAG_START}${mergeTag.variable_name}${MERGE_TAG_END}`
			);
	});
	return textToReturn;
};

export const cleanUpTextAndReplaceMergeTagsWithId = (
	mergeTagVariables: MergeTagVariable[],
	text: string
): string => {
	return replaceMergeTagsWithId(mergeTagVariables, text).replace(
		/\uFEFF/g,
		""
	);
};

export const computeMergeTagOptions = ({
	mergeTagVariables,
	MergeTagListToSkip = [],
	isDocumentMessage,
	hideReceiverMergeTag,
	MergeTagChildrenListToSkip = []
}: ComputeMergeTagProps) => {
	const mergeTagOrder = {
		Document: 0,
		Client: 1,
		ClientCompany: 2,
		Opportunity: 3,
		MyCompany: 4
	};

	const childrenMergeTagsOrder = {
		Document: {
			ID: 0,
			Name: 1,
			Status: 2,
			CreatedAt: 3,
			ExpiresAt: 4,
			"Owner.Name": 5,
			"Owner.Email": 6,
			"Owner.Phone": 7,
			"Owner.FirstName": 8,
			"Owner.LastName": 9,
			"Owner.JobTitle": 10,
			"Total.OneTime": 11,
			"Total.Monthly": 12,
			"Total.Quarterly": 13,
			"Total.SemiAnnual": 14,
			"Total.Annual": 15,
			"Total.GrandTotal": 16
		},
		Client: {
			ID: 0,
			Name: 1,
			Phone: 2,
			Fax: 3,
			DateAcquired: 4,
			Contact: 5,
			Market: 6,
			Campaign: 7,
			Website: 8,
			Facebook: 9,
			Twitter: 10,
			Linkedin: 11,
			"BillingAddress.Street": 12,
			"BillingAddress.AddressLine1": 13,
			"BillingAddress.AddressLine2": 14,
			"BillingAddress.City": 15,
			"BillingAddress.State": 16,
			"BillingAddress.Pincode": 17,
			"BillingAddress.Country": 18,
			"ShipToAddress.AddressLine1": 19,
			"ShipToAddress.AddressLine2": 20,
			"ShipToAddress.City": 21,
			"ShipToAddress.State": 22,
			"ShipToAddress.Pincode": 23,
			"ShipToAddress.Country": 24,
			"ShipToAddress.Phone": 25
		},
		Opportunity: {
			ID: 0,
			Name: 1,
			Stage: 2,
			Type: 3,
			EstimatedValue: 4,
			EstimatedCloseDate: 5,
			CloseDate: 6,
			ClientPoNumber: 7,
			Referral: 8
		},
		MyCompany: {
			Name: 0,
			Phone: 1,
			"BillingAddress.Street": 2,
			"BillingAddress.AddressLine1": 3,
			"BillingAddress.AddressLine2": 4,
			"BillingAddress.City": 5,
			"BillingAddress.State": 6,
			"BillingAddress.Pincode": 7,
			"BillingAddress.Country": 8
		}
	};

	MergeTagListToSkip = hideReceiverMergeTag ? MergeTagListToSkip : [];

	const allSplitKeys = mergeTagVariables.map(
		mergeTagVariable => mergeTagVariable.variable_name.split(".")[0]
	);
	const firstListItems = uniq(allSplitKeys).filter(
		item => !(!isDocumentMessage && MergeTagListToSkip.includes(item))
	);

	const toRenderInFirstExpanders = firstListItems.filter(
		keyToCount =>
			allSplitKeys.reduce(
				(count, value) => (value === keyToCount ? count + 1 : count),
				0
			) > 1
	);
	const getLabelForKey = (key: string, secondLevel: boolean) => {
		if (secondLevel) {
			return key;
		} else {
			if (toRenderInFirstExpanders.includes(key)) {
				return key;
			} else {
				return mergeTagVariables.find(
					variable =>
						variable.display_name.startsWith(`${key}.`) ||
						variable.display_name === key
				)?.display_name;
			}
		}
	};
	const getValueForKey = (key: string) => {
		if (!toRenderInFirstExpanders.includes(key)) {
			return (
				mergeTagVariables.find(
					variable => variable.variable_name === key
				) ||
				mergeTagVariables.find(variable =>
					variable.variable_name.startsWith(`${key}.`)
				)
			)?.variable_name;
		}
		return key;
	};
	const getChildrenForKey = (key: string) => {
		if (toRenderInFirstExpanders.includes(key)) {
			return {
				children: mergeTagVariables
					.filter(
						variable =>
							variable.display_name.startsWith(`${key}.`) &&
							!MergeTagChildrenListToSkip.includes(
								variable.variable_name
							) &&
							/**
							 * Will remove this additinal condidition later,
							 * once the BE complete the migration to remove HalfYearly mergetags
							 */
							variable.display_name !==
								"Opportunity.HalfYearlyRevenue" &&
							variable.display_name !==
								"Opportunity.HalfYearlyCost" &&
							variable.display_name !==
								"Document.Total.HalfYearly"
					)
					.map(tagKey => {
						const mergeTagLabel = getLabelForKey(
							tagKey.display_name.replace(`${key}.`, ""),
							true
						);

						let childKey = mergeTagLabel;

						if (
							mergeTagLabel &&
							mergeTagLabel.match(/Contact[.]\d+[.]\S+/gm)
						) {
							childKey = "Contact";
						}

						return {
							label: mergeTagLabel || "",
							value: tagKey.variable_name,
							id:
								(
									childrenMergeTagsOrder as unknown as Record<
										string,
										string
									>
								)[key]?.[childKey as any] || ""
						};
					})
					.sort((childMergeTagA, childMergeTagB) => {
						if (!childMergeTagA || !childMergeTagB) {
							return 0;
						}
						return (
							Number(childMergeTagA.id) -
							Number(childMergeTagB.id)
						);
					})
					.map(childMergeTag => ({
						label: childMergeTag.label,
						value: childMergeTag.value
					}))
			};
		} else {
			return {};
		}
	};
	const mergeTags = firstListItems
		.map(listItem => {
			return {
				label: getLabelForKey(listItem, false),
				value: getValueForKey(listItem),
				id: (mergeTagOrder as unknown as Record<string, number>)[
					getLabelForKey(listItem, false) as string
				],
				...getChildrenForKey(listItem)
			};
		})
		.sort((mergeTagA, mergeTagB) => {
			return mergeTagA.id - mergeTagB.id;
		})
		.map(mergeTag => ({
			label: mergeTag.label,
			value: mergeTag.value,
			children: mergeTag.children
		}));

	return mergeTags;
};

export const updateMergeTagsDisplayName = (
	mergeTagVariables: MergeTagVariable[],
	text: string
): string => {
	let textToReturn = text;
	if (!mergeTagVariables) mergeTagVariables = [];
	const matchMergeTagsRegex =
		/<span[^>]*?class="merge-tag mceNonEditable"[^>]*>({{\S+}})<\/span>/g;
	// {{  }} matching anything inside brackets as custom merge tags can be without dot

	const matchedTags: RegExpMatchArray | null =
		text?.match(matchMergeTagsRegex);

	matchedTags?.forEach(tag => {
		const mergeTagRegex = /{{(\S+)}}/;
		const mergeTag = mergeTagRegex.exec(tag)?.[1];
		const isValidMergeTag = mergeTagVariables.find(
			currentMergeTag =>
				currentMergeTag.variable_name === mergeTag ||
				currentMergeTag.display_name === mergeTag
		);

		if (!isValidMergeTag) {
			const cleanedTag = tag.replace('/"', '"');
			textToReturn = textToReturn.replace(cleanedTag, "");
		}
	});
	const isPreview = window.location?.pathname?.split("/")?.[2] === "view";

	mergeTagVariables.forEach(mergeTag => {
		if (mergeTag.data_type === MergeTagVariableDataType.URL && isPreview) {
			if (!mergeTag.value) {
				textToReturn = textToReturn
					.split(
						`${MERGE_TAG_START}${mergeTag.variable_name}${MERGE_TAG_END}`
					)
					.join("");
				return;
			}
			let mergeTagValue = mergeTag.value;
			if (
				!mergeTag.value.startsWith("http://") &&
				!mergeTag.value.startsWith("https://")
			) {
				mergeTagValue = `http://${mergeTag.value}`;
			}
			textToReturn = textToReturn
				.split(
					`${MERGE_TAG_START}${mergeTag.variable_name}${MERGE_TAG_END}`
				)
				.join(
					`<a href="${mergeTagValue}" target="_blank">${mergeTagValue}</a>`
				);
		} else {
			textToReturn = textToReturn
				.split(
					`${MERGE_TAG_START}${mergeTag.variable_name}${MERGE_TAG_END}`
				)
				.join(
					`${MERGE_TAG_START}${mergeTag.display_name}${MERGE_TAG_END}`
				);
		}
	});
	return textToReturn;
};

const splitAndKeep = function (separator: string, str: string) {
	return str.split(new RegExp(`(${separator})`, "g"));
};

export const MERGETAGSREGEX = /{{([^}}]+)}}/g;
export const convertTextToMergeTag = (
	mergeTagVariables: MergeTagVariable[],
	text: string,
	pureText?: boolean
): string => {
	const matchMergeTagsRegex = pureText
		? MERGETAGSREGEX
		: /<span[^>]*?class="merge-tag mceNonEditable"[^>]*>({{\S+}})<\/span>/g;

	const matchedTags: RegExpMatchArray | null =
		text?.match(matchMergeTagsRegex);

	let toBeReturnedText: string[] = [];

	const map: Record<string, boolean> = {};

	matchedTags?.forEach(tag => {
		if (!(tag in map)) {
			map[tag] = true;
			const res = splitAndKeep(tag, text);
			res.forEach(tagOrText => {
				if (tagOrText === tag) {
					const mergeTagRegex = /{{(.*?)}}/;
					const mergeTag = mergeTagRegex.exec(tag)?.[1];
					const originalMergeTag = mergeTagVariables.find(
						currentMergeTag =>
							currentMergeTag.variable_name === mergeTag ||
							currentMergeTag.display_name === mergeTag
					);

					if (originalMergeTag) {
						tagOrText =
							`<span class="merge-tag mceNonEditable" contenteditable="false" data-contenteditable="false" data-id="${originalMergeTag.variable_name}">${MERGE_TAG_START}${originalMergeTag.variable_name}${MERGE_TAG_END}</span>` +
							"\ufeff";
					}
				}

				toBeReturnedText.push(tagOrText);
			});
			text = toBeReturnedText.join("");
			toBeReturnedText = [];
		}
	});
	return text;
};

export const getMergeTagsEntityType = (
	trigger_entity_type: TriggerActionEntityTypes
): EntityType => {
	switch (trigger_entity_type) {
		case ZomentumEntities.Client:
			return EntityType.Client;
		case ZomentumEntities.Document:
			return EntityType.Document;
		case ZomentumEntities.User:
			return EntityType.User;
		default:
			return isVendorApp()
				? EntityType.VendorOpportunity
				: EntityType.Opportunity;
	}
};

export const checkForValidMergeTag = (
	mergeTagVariables: MergeTagVariable[],
	text: string,
	checkForTag: string
) => {
	let isValid = false;
	if (!mergeTagVariables) mergeTagVariables = [];
	const matchMergeTagsRegex =
		/<span[^>]*?class="merge-tag mceNonEditable"[^>]*>({{\S+}})<\/span>/g;
	// {{  }} matching anything inside brackets as custom merge tags can be without dot

	const matchedTags: RegExpMatchArray | null =
		text?.match(matchMergeTagsRegex);

	matchedTags?.forEach(tag => {
		const mergeTagRegex = /{{(\S+)}}/;
		const mergeTag = mergeTagRegex.exec(tag)?.[1];
		const isValidMergeTag = mergeTagVariables.find(
			currentMergeTag =>
				currentMergeTag.variable_name === mergeTag ||
				currentMergeTag.display_name === mergeTag
		);

		if (isValidMergeTag) {
			const valiable = mergeTag?.split(".");
			if (valiable?.includes(checkForTag)) {
				isValid = true;
			}
		}
	});
	return isValid;
};

export const mergeTagOnSubmit = (
	window: ZomentumWindow,
	merge_tags: MergeTagVariable[],
	key: string
) => {
	const editorRange = window.tinyMCE.activeEditor.selection.getRng();
	const node = editorRange.commonAncestorContainer;
	const newRange = document.createRange();
	newRange.selectNodeContents(node);
	newRange.setStart(node, editorRange.endOffset - MERGE_TAG_START.length);
	newRange.setEnd(node, editorRange.endOffset);
	newRange.deleteContents();
	window.tinyMCE.activeEditor.focus();
	const mergeTagDisplayName = merge_tags.find(
		mergeTag => mergeTag.variable_name === key
	)?.display_name;

	window.tinyMCE.activeEditor.insertContent(
		`<span class="merge-tag mceNonEditable" contentEditable="false" data-mce-contenteditable="false" data-id="${key}">${MERGE_TAG_START}${mergeTagDisplayName}${MERGE_TAG_END}</span>\ufeff `,
		{
			format: "html"
		}
	);
};
